import axios from 'axios';
import Lottie from 'lottie-react';
import { useState } from 'react';

import handleAxiosError from 'lib/errors/handleAxiosError';
import useApiLoader from 'lib/hooks/useApiLoader';
import { getUriExtension } from 'lib/utils/url';

enum Status {
  Loading,
  Ready,
  Errored,
}

const LottiePictograph = ({
  assetUrl,
  height,
  fallback,
  'aria-label': ariaLabel,
}: {
  assetUrl: string;
  height?: string;
  fallback: JSX.Element;
  'aria-label'?: string;
}) => {
  const [animationData, setAnimationData] = useState<Record<string, unknown>>();
  const [status, setStatus] = useState<Status>(Status.Loading);

  useApiLoader({
    loader: async () => {
      if (getUriExtension(assetUrl) !== 'json') {
        setStatus(Status.Errored);
        return;
      }

      try {
        const { data } = await axios.get(assetUrl);
        setAnimationData(data);
      } catch (error) {
        handleAxiosError({ error, method: 'GET', uri: assetUrl });
      }

      setStatus(Status.Ready);
    },
    deps: [assetUrl],
    onError: () => setStatus(Status.Errored),
    onNetworkError: () => setStatus(Status.Errored),
  });

  switch (status) {
    case Status.Loading:
      return null;
    case Status.Errored:
      return fallback;
    case Status.Ready:
      return (
        <Lottie
          role='img'
          aria-label={ariaLabel}
          animationData={animationData}
          style={{
            // Context: INC-427
            // Lottie animations that are larger than ~1100px can cause crashes
            // on WebView. There's no legitimate reason to render such a large
            // Lottie anyways, because on the mobile layout the max screen
            // width is 768px, and on the desktop layout the max widget width
            // is 386px.
            height: `min(${height}, 1000px)`,
            width: 'auto',
          }}
        />
      );
  }
};

export default LottiePictograph;
